/**
 * Format: Product
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API product responses.
 *
 */
import { formatImages } from '~/format/format-image'
import { formatMedia } from '~/format/format-media'
import { formatOptions } from '~/format/format-option'
import { formatProductPriceRange } from '~/format/format-price'
import { formatVariants } from '~/format/format-variant'
import { convertToHandle } from '~/helpers/convert'
import { decodeApiId, transformEdges } from '~/helpers/graphql'
import { handlePreOrderProductCurrentDate, handlePreOrderProductPastDate } from '~/helpers/general'

/**
 * Format product.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/Product
 * @param {Object} response - GraphQL response.
 * @param {Object} [collection] - Formatted collection object.
 * @returns {Object}
 */
export function formatProduct(response, collection) {
  const product = response.product ? response.product : response

  const formattedProduct = {
    available: product.available,
    badges: formatProductBadges(product),
    compareAtPrice: formatProductPriceRange(product.compareAtPriceRange),
    description: product.descriptionHtml,
    giftCard: product.giftCard,
    handle: product.handle,
    id: decodeApiId(product.id),
    media: formatMedia(product),
    metafields: {
      bookthatapp: getMetafieldValue('config', product.metafields),
      preOrderAvailableDate: getMetafieldValue('pre_order', product.metafields),
      preOrderClosingDate: getMetafieldValue('pre_order_closing_date', product.metafields),
      preOrderAvailableMessage: getMetafieldValue('pre_order_available_message', product.metafields),
      preOrderClosingMessage: getMetafieldValue('pre_order_closing_message', product.metafields),
    },
    personalisationCrossSell: getMetafieldValue('personalised_cross_sell', product.metafields),
    price: formatProductPriceRange(product.priceRange),
    promoTag: getMetafieldValue('promo_tag', product.metafields),
    promoTagAltStyle: Boolean(getMetafieldValue('promo_tag_alt_style', product.metafields)),
    specifications: formatProductSpecifications(product),
    tags: product.tags,
    title: product.title,
    type: product.type,
    variants: formatVariants(product, collection),
    vendor: product.vendor,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  const featuredImages = formatImages(product, transformEdges(product.featuredImages))

  formattedProduct.featuredImage = featuredImages.length
    ? featuredImages[0]
    : featuredImages

  formattedProduct.secondaryImage = featuredImages.length > 1
    ? featuredImages[1]
    : {}

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  formattedProduct.options = formatOptions(product, formattedProduct.variants)

  formattedProduct.selectedOrFirstAvailableVariant =
    getSelectedOrFirstAvailableVariant(formattedProduct)

  const url = collection
    ? `${cnvs.routes.normalisedRoot}collections/${collection.handle}/products/${product.handle}`
    : `${cnvs.routes.normalisedRoot}products/${product.handle}`

  formattedProduct.url = formatUrl(url, product.trackingParameters)

  formattedProduct.limits = formatProductDefaultLimits(formattedProduct)

  formattedProduct.personalisation = formatProductPersonalisation(product)

  return formattedProduct
}

/**
 * Get selected or first available variant.
 * - 'Selected' based on URL parameter.
 * - If no variants available then return first.
 * @param {Object} product - Formatted product object.
 * @returns {Object}
 */
function getSelectedOrFirstAvailableVariant(product) {
  const urlSearchParams = new URLSearchParams(location.search)

  const variantId = urlSearchParams.has('variant')
    ? urlSearchParams.get('variant')
    : false

  const selectedVariant = product.variants.find((variant) => {
    if (variantId) {
      return variant.id === Number(variantId)
    }

    return variant.available
  })

  if (selectedVariant) {
    return selectedVariant
  }

  return product.variants[0]
}

/**
 * Format response URL with tracking parameters.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/Product#field-product-trackingparameters
 * @param {String} url - The URL of the object.
 * @param {Null|String} [trackingParameters] - Response tracking parameters.
 * @returns {String}
 */
export function formatUrl(url, trackingParameters) {
  if (!trackingParameters) {
    return url
  }

  const trackingSearchParams = new URLSearchParams(trackingParameters)
  const isRelativeUrl = !url.includes('//')

  /**
   * Handle relative URL
   * - If passed `url` is relative, process and return a relative URL.
   */
  if (isRelativeUrl) {
    const [baseUrl, searchParams] = url.split('?')

    const urlSearchParams = new URLSearchParams({
      ...Object.fromEntries(new URLSearchParams(searchParams)),
      ...Object.fromEntries(trackingSearchParams),
    })

    if (!urlSearchParams.size) {
      return url
    }

    return `${baseUrl}?${urlSearchParams.toString()}`
  }

  /**
   * Handle non-relative URL.
   * - Storefront API doesn't include https in URL but new URL() requires it.
   */
  const localUrl = `https://${url.split('//')[1]}`
  const urlObject = new URL(localUrl)

  const parameters = new URLSearchParams({
    ...Object.fromEntries(urlObject.searchParams),
    ...Object.fromEntries(trackingSearchParams),
  })

  if (!parameters.size) {
    return url
  }

  const href = urlObject.href
    .split('?')[0]
    .replace('https:', '')

  return `${href}?${parameters.toString()}`
}

/**
 * Get the badges from metafields.
 * - Defines with the style is standard or alternate.
 * @param {Object} product - product object from Shopify.
 * @returns {Array}
 */
function formatProductBadges(product) {
  const metafields = product.metafields
  const badges = []

  if (getMetafieldValue('badge_1', metafields)) {
    badges.push(getMetafieldValue('badge_1', metafields))
  }

  if (getMetafieldValue('badge_2', metafields)) {
    badges.push(getMetafieldValue('badge_2', metafields))
  }

  if (!product.available) {
    badges.push('oos')
  }

  // Handle Pre-Order badge.
  let isPreOrder = false
  const hasPreOrderTag = product.tags?.some((tag) => tag.includes('Pre-Order'))
  const preOrderAvailableDate = getMetafieldValue('pre_order', metafields)

  if (hasPreOrderTag) {
    isPreOrder = true
  }

  if (
    handlePreOrderProductPastDate(preOrderAvailableDate) ||
    handlePreOrderProductCurrentDate(preOrderAvailableDate)
  ) {
    isPreOrder = false
  }

  if (isPreOrder) {
    badges.push('pre-order')
  }

  return badges
}

/**
 * Get the default product limitations.
 * - Non customer data based informations
 * - Creates a base object that will later be updated via EventBus.
 * - Collectors: Product is exclusive to Collectors Club members
 * - Limit Per Customer: customer can only purchase the product x amount
 *   of times. The limit is against current cart qty and past orders.
 *   (previously defined as 'limit per order')
 * - One Per Order: as above, but limited to one.
 *
 * - Tags are synced from Patchworks.
 *
 * @param {Object} product - formattedProduct object.
 * @returns {Array}
 */
function formatProductDefaultLimits(product) {
  const limitTags = getProductTagValues(product.tags, 'LIMIT_PER_ORDER')
  const perCustomer = limitTags.length ? parseInt(limitTags[0]) : 999

  const limits = {
    collectors: Boolean(getProductTagValues(product.tags, 'COLLECTORS_ONLY').length),
    collectorsEarly: Boolean(getProductTagValues(product.tags, 'COLLECTORS_EARLY').length),
    collectorsPreview: Boolean(getProductTagValues(product.tags, 'COLLECTORS_PREVIEW').length),
    inCart: false,
    onePerOrder: product.tags?.some((tag) => tag.includes('LIMIT_PER_ORDER')),
    perCustomer,
    purchasedPreviously: false,
    remaining: perCustomer,
  }

  return limits
}

/**
 * Get all tags that belong to a particular split tag.
 * E.g. `badge: sale; badge: new-in`
 *
 * @example
 * const foo = getTaggedValues('badge')
 * @param {Array} tags - array of product tags.
 * @param {String} label - the label of the tag.
 * @returns {Array}
 */
function getProductTagValues(tags, label, split = ':') {
  const labelSplit = `${label}${split}`

  return tags.map((tag) => {
    if (!tag.startsWith(labelSplit)) {
      return false
    }

    return tag.split(labelSplit)[1].trim()
  }).filter(Boolean)
}

/**
 * Get a speicifc metafield from the metafields.
 *
 * @example
 * const promoTag = getMetafieldValue('promo_tag', metafields)
 * @param {String} key - the metafield to get.
 * @param {Array} metafields - array of product metafields.
 * @returns {Array}
 */
function getMetafieldValue(key, metafields) {
  const metafield = metafields.find((meta) => {
    return meta?.key === key && meta?.value !== 'false'
  })

  return metafield?.value || false
}

/**
 * Format product specifications
 * - Grab product specifications from metafields and tags.
 * @param {Object} product - product object
 * @returns {Array}
 */
function formatProductSpecifications(product) {
  const specifications = []
  const metafieldSpecifications = ['height', 'width', 'diameter', 'capacity', 'length', 'care_instructions', 'manufacturer_country', 'made_from']

  metafieldSpecifications.forEach((key) => {
    const value = getMetafieldValue(key, product.metafields)
    if (value) {
      specifications.push({
        specification: key,
        value,
      })
    }
  })

  // Handle Specifications from tags.
  const natureTag = product.tags.find((tag) => tag.includes('NATURE:'))
  if (natureTag) {
    const tagValue = natureTag.split(':').pop()
    specifications.push({
      specification: 'nature',
      value: tagValue,
    })
  }

  const shapeTag = product.tags.find((tag) => tag.includes('SHAPE_DESCRIPTION:'))
  if (shapeTag) {
    const tagValue = shapeTag.split(':').pop()
    specifications.push({
      specification: 'shape',
      value: tagValue,
    })
  }

  return specifications
}

/**
 * Format personalisation data.
 * @param {Object} product product data.
 * @returns {Object} formatted personalisation data.
 */
function formatProductPersonalisation(product) {
  const isEnabled = product.tags.some((tag) => {
    return ['MARK_IF_PERSONALISED: Yes', 'NATURE: Personalised Pottery'].includes(tag)
  })

  const locations = formatPersonalisationLocations(product)

  const introMessage = getMetafieldValue('personalisation_intro', product.metafields)

  return {
    enabled: isEnabled,
    locations,
    introMessage,
  }
}

/**
 * Format personalisation locations.
 * - Get the enabled locations and their character limits.
 * @param {Object} product product data.
 * @returns {Object} formatted personalisation data.
 */
function formatPersonalisationLocations(product) {
  const tagPrefix = 'PERSONALISATION:'
  const locationTags = product.tags.filter((tag) => {
    return tag.startsWith(tagPrefix)
  })

  const locations = locationTags.map((tag) => {
    const name = tag.split(tagPrefix)[1].trim()
    const characterLimit = getPersonalisationLocationCharacterLimits(name, product.metafields)
    return {
      name,
      characterLimit,
    }
  })

  return locations
}

/**
 * Get character limit for a personalisation location from metafields.
 * - Defaults to 20.
 * @param {String} name - personalisation location name.
 * @param {Array} metafields - product metafields.
 * @returns {Number} character limit.
 */
function getPersonalisationLocationCharacterLimits(name, metafields) {
  const metafieldNameHandle = convertToHandle(name).replaceAll('-', '_')
  const metafieldName = `${metafieldNameHandle}_characters`

  const value = getMetafieldValue(metafieldName, metafields)

  return value ? Number(value) : 20
}
